// LangkahAwalCampaignDashboard.jsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, CartesianGrid
} from "recharts";
import { baseUrl } from "../config/const";

// ================= Utils =================
const formatIDR = (num) => {
  if (!num) return "Rp 0";
  return "Rp " + Number(num).toLocaleString("id-ID");
};

// ================= KPI Card =================
const KPICard = ({ title, value, subtitle }) => (
  <div style={{ background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
    <div style={{ fontSize: 12, color: "#666" }}>{title}</div>
    <div style={{ fontSize: 24, fontWeight: "bold", marginTop: 4 }}>{value}</div>
    {subtitle && <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>{subtitle}</div>}
  </div>
);

// ================= Target Progress Card =================
const TargetCard = ({ title, actual = 0, target = 0, isPercent, isCurrency }) => {
  const progress = target ? actual / target : 0;
  const pct = Math.min(100, Math.round(progress * 100));

  const format = v => {
    if (isPercent) return (v * 100).toFixed(2) + "%";
    if (isCurrency) return formatIDR(v);
    return Number(v || 0).toLocaleString("id-ID");
  };

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 12, color: "#666" }}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: "bold" }}>
        {format(actual)} / {format(target)}
      </div>

      <div style={{ background: "#eee", borderRadius: 8, height: 10, marginTop: 6 }}>
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 8,
            background: pct >= 100 ? "#2ecc71" : pct >= 70 ? "#f1c40f" : "#e74c3c"
          }}
        />
      </div>

      <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
        {pct}% of target
      </div>
    </div>
  );
};

// ================= Main Dashboard =================
export default function LangkahAwalCampaignDashboard() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchKPI = async (override = {}) => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/getLangkahAwalKPI`, { params: override });
      setData(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load KPI");
    } finally {
      setLoading(false);
    }
  };

  const runSnapshot = async () => {
    try {
      setLoading(true);
      await axios.post(`${baseUrl}/langkahawalsnapshotkpi`);
      await fetchKPI(startDate && endDate ? { start: startDate, end: endDate } : {});
      alert("Snapshot completed");
    } catch (err) {
      console.error(err);
      alert("Snapshot failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKPI();
  }, []);

  if (loading) return <div>Loading KPI...</div>;
  if (error) return <div>{error}</div>;
  if (!data) return null;

  // ================= Derived Data =================
  const totalNewCustomers = data.hero_first_purchase
    .reduce((sum, r) => sum + Number(r.new_customers), 0);

  const heroAcqChart = data.hero_first_purchase.map(r => ({
    name: r.first_purchase_collection,
    value: Number(r.new_customers)
  }));

  const heroQtyChart = Object.entries(data.heroProductQty.by_collection)
    .map(([k, v]) => ({ name: k, value: v }));

  const repeatDistChart = data.time_to_repeat_distribution.map(r => ({
    name: r.bucket,
    value: Number(r.customers)
  }));

  const productInfluenceChart = data.product_influence.map(r => ({
    name: r.repeat_collection,
    value: Number(r.total_repeat_orders)
  }));

  const storePerfChart = data.first_purchase_stores_performance.map(r => ({
    name: r.first_purchase_store,
    value: Number(r.new_customers)
  }));

  // ===== Monthly Charts =====
  const monthlyNewCustomers = (data.monthly?.monthly_new_customers || []).map(r => ({
    month: new Date(r.month).toLocaleDateString("id-ID", { month: "short", year: "numeric" }),
    value: Number(r.new_customers)
  }));

  const monthlyRevenue = (data.monthly?.monthly_first_revenue || []).map(r => ({
    month: new Date(r.month).toLocaleDateString("id-ID", { month: "short", year: "numeric" }),
    value: Number(r.first_purchase_revenue)
  }));

  const monthlyRepeatRate = (data.monthly?.monthly_repeat_rate || []).map(r => ({
    month: new Date(r.month).toLocaleDateString("id-ID", { month: "short", year: "numeric" }),
    value: Number(r.repeat_rate) * 100
  }));

  const targets = data.targets || {};

  // ===== Target Sections (NEW) =====
  // helper
  const humanizeLabel = (str = "") => {
  return str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
};
  const heroQtyTargetRows = Object.entries(targets.hero_qty_by_collection || {})
    .map(([collection, obj]) => ({
      label: humanizeLabel(collection),
      actual: obj.actual,
      target: obj.target
    }));
  const timeToRepeatTargetRows = Object.values(targets.time_to_repeat_distributions || {});

  // ================= Render =================
  return (
    <div style={{ padding: 24, background: "#f5f7fa", minHeight: "100vh" }}>
      <h2>Langkah Awal Campaign Dashboard</h2>

      {/* Toolbar - 1 Row */}
      <div style={{  display: "flex",
        flexWrap: "nowrap",     // ⬅️ penting
        gap: 8,
        alignItems: "center",
        marginBottom: 12,
        overflowX: "auto"  }}>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: 8, borderRadius: 6 , minWidth: 140}} />
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: 8, borderRadius: 6 , minWidth: 140 }} />

        <button
          onClick={() => fetchKPI({ start: startDate, end: endDate })}
          disabled={loading || !startDate || !endDate}
          style={{ padding: "8px 14px", borderRadius: 8, minWidth: 120 }}
        >
          Apply Filter
        </button>

        <button onClick={runSnapshot} disabled={loading} style={{ padding: "8px 14px", borderRadius: 8 }}>
          {loading ? "Running..." : "Run Snapshot"}
        </button>
      </div>

      <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>
        Campaign Period: {new Date(data.campaign.default_start).toLocaleDateString()} - {new Date(data.campaign.default_end).toLocaleDateString()}
        <br />
        Showing: {new Date(data.showing_date.start).toLocaleDateString()} - {new Date(data.showing_date.end).toLocaleDateString()}
        {data.showing_date.is_override ? " (Custom)" : " (Campaign Period)"}
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16, marginBottom: 24 }}>
        <KPICard title="New Customers (Hero)" value={totalNewCustomers} />
        <KPICard title="Repeat Rate" value={(data.repeat_rate_ratio_after_first_hero_product_purchase * 100).toFixed(2) + "%"} />
        <KPICard title="Median Days to 2nd Purchase" value={data.median_days_to_second_purchase + " days"} />
        <KPICard title="Upgrade Rate" value={(data.upgrade_rate_ratio * 100).toFixed(2) + "%"} />
        <KPICard title="Hero Product Qty" value={data.heroProductQty.total} subtitle="pairs sold" />
        <KPICard title="Total Revenue" value={formatIDR(data.revenue.total_revenue)} subtitle={`Avg / Customer: ${formatIDR(data.revenue.avg_revenue)}`} />
      </div>

      {/* Target vs Actual (Core KPIs) */}
      <h3>Target vs Actual</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
        <TargetCard title="New Customers" {...targets.new_customers} />
        <TargetCard title="Repeat Rate" {...targets.repeat_rate} isPercent />
        <TargetCard title="First Purchase Revenue" {...targets.first_purchase_revenue} isCurrency />
        <TargetCard title="Total Cohort Revenue" {...targets.total_cohort_revenue} isCurrency />
      </div>

      {/* Target vs Actual - Hero Qty by Collection (NEW) */}
      <h3>Hero Qty by Collection vs Target</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
        {heroQtyTargetRows.map((row, idx) => (
          <TargetCard
            key={idx}
            title={row.label}
            actual={row.actual}
            target={row.target}
          />
        ))}
      </div>

      {/* Target vs Actual - Time to Repeat Distribution (NEW) */}
      <h3>Time to Repeat vs Target</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
        {timeToRepeatTargetRows.map((row, idx) => (
          <TargetCard
            key={idx}
            title={row.label}
            actual={row.actual}
            target={row.target}
          />
        ))}
      </div>

      {/* Monthly Trends */}
      <h3>Monthly Trends</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
          <h4>Monthly New Customers</h4>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthlyNewCustomers}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#2ecc71" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
          <h4>Monthly First Purchase Revenue</h4>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={v => formatIDR(v)} />
              <Line type="monotone" dataKey="value" stroke="#3498db" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, padding: 16, marginBottom: 24 }}>
        <h4>Monthly Repeat Rate (%)</h4>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={monthlyRepeatRate}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={v => v.toFixed(2) + "%"} />
            <Line type="monotone" dataKey="value" stroke="#e67e22" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Existing Charts (Hero, Retention, Store) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
          <h4>Hero Acquisition (New Customers)</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={heroAcqChart}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
          <h4>Hero Product Qty Sold</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={heroQtyChart}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
          <h4>Time to Repeat Distribution</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={repeatDistChart}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
          <h4>Product Influence (Repeat Drivers)</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={productInfluenceChart.slice(0, 8)}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
        <h4>First Purchase Store Performance</h4>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={storePerfChart.slice(0, 10)}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
